import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  PERIOD_DAYS,
  type Consumption,
  type Period,
  type RecentRequest,
  type UsagePoint,
  type UsageSeries,
  type UsageSummary,
} from "@/types/dashboard";

// Data hooks for the dashboard. Each hook owns its endpoint choice
// (admin vs. user scope) and maps the payload to a view-model type so
// widgets stay free of transport details.

const STALE = 60 * 1000;

// Raw row shapes from the backend (only the fields we read).
interface DailyRow {
  date: string;
  count?: number | string;
  promptTokens?: number | string;
  completionTokens?: number | string;
}

interface ModelRow {
  model: string;
  provider: string;
  requests?: number | string;
  totalTokens?: number | string;
  totalCost?: number | string;
  cost?: number | string;
}

interface UsageRow {
  id: string | number;
  provider?: string;
  model?: string;
  totalTokens?: number | string;
  promptTokens?: number | string;
  completionTokens?: number | string;
  requestDuration?: number | string;
  successful?: boolean;
  createdAt: string;
  username?: string;
  user?: { username?: string };
}

const toNum = (v: unknown) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? 0));
  return Number.isFinite(n) ? n : 0;
};

const localDay = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Backend DATE / DATE_TRUNC values arrive as "YYYY-MM-DD" or ISO timestamps.
const dayKey = (raw: string) => (/^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : localDay(new Date(raw)));

export function useUsageSummary(isAdmin: boolean) {
  return useQuery({
    queryKey: ["dashboard", "summary", isAdmin],
    staleTime: STALE,
    queryFn: async (): Promise<UsageSummary> => {
      const r = await api.get(isAdmin ? "/admin/usage/summary" : "/auth/me/usage/summary");
      return {
        totalRequests: toNum(r.totalRequests),
        monthlyQuota: toNum(r.monthlyQuota),
        successRate: toNum(r.successRate),
        todayRequests: toNum(r.todayRequests),
        tokens: { input: toNum(r.tokens?.input), output: toNum(r.tokens?.output) },
        providers: Array.isArray(r.providers) ? r.providers : [],
      };
    },
  });
}

/** Daily series for the selected period plus totals for the period before it. */
export function useUsageSeries(period: Period, isAdmin: boolean) {
  return useQuery({
    queryKey: ["dashboard", "series", period, isAdmin],
    staleTime: STALE,
    queryFn: async (): Promise<UsageSeries> => {
      const days = PERIOD_DAYS[period];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const start = new Date(today);
      start.setDate(start.getDate() - (days * 2 - 1));

      const qs = `?startDate=${start.toISOString()}&endDate=${new Date().toISOString()}&groupBy=day`;
      const r = await api.get(isAdmin ? `/admin/usage/daily${qs}` : `/auth/me/usage/daily${qs}`);

      const byDay = new Map<string, UsagePoint>();
      for (let i = 0; i < days * 2; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        const key = localDay(d);
        byDay.set(key, { date: key, requests: 0, promptTokens: 0, completionTokens: 0 });
      }

      // User rows: { date, count, promptTokens, completionTokens }
      // Admin rows: { date, provider, count } — one row per provider per day
      const rows: DailyRow[] = (isAdmin ? r.statistics : r.dailyUsage) || [];
      for (const row of rows) {
        const p = byDay.get(dayKey(String(row.date)));
        if (!p) continue;
        p.requests += toNum(row.count);
        p.promptTokens += toNum(row.promptTokens);
        p.completionTokens += toNum(row.completionTokens);
      }

      const all = Array.from(byDay.values());
      const prev = all.slice(0, days);
      const points = all.slice(days);
      const totals = (list: UsagePoint[]) => ({
        requests: list.reduce((a, p) => a + p.requests, 0),
        tokens: list.reduce((a, p) => a + p.promptTokens + p.completionTokens, 0),
      });

      return {
        points,
        current: totals(points),
        previous: totals(prev),
        hasTokens: !isAdmin,
      };
    },
  });
}

export function useConsumption(period: Period, isAdmin: boolean) {
  return useQuery({
    queryKey: ["dashboard", "consumption", period, isAdmin],
    staleTime: STALE,
    queryFn: async (): Promise<Consumption> => {
      const r = await api.get(
        isAdmin ? `/admin/analytics/consumption?timeRange=${period}` : `/auth/me/consumption?timeRange=${period}`
      );
      const models = ((r.modelBreakdown || []) as ModelRow[]).map((m) => ({
        model: m.model,
        provider: m.provider,
        requests: toNum(m.requests),
        totalTokens: toNum(m.totalTokens),
        totalCost: toNum(m.totalCost ?? m.cost),
      }));
      return {
        totalCost: toNum(r.summary?.totalCost),
        totalTokens: toNum(r.summary?.totalTokens),
        totalRequests: toNum(r.summary?.totalRequests),
        models,
      };
    },
  });
}

export function useRecentRequests(isAdmin: boolean, limit = 7) {
  return useQuery({
    queryKey: ["dashboard", "recent", isAdmin, limit],
    staleTime: STALE,
    queryFn: async (): Promise<RecentRequest[]> => {
      const r = await api.get(isAdmin ? `/admin/usage?limit=${limit}` : `/auth/me/usage?limit=${limit}`);
      const rows: UsageRow[] = Array.isArray(r.data) ? r.data : r.usageRecords || r.records || [];
      return rows.slice(0, limit).map((u) => ({
        id: String(u.id),
        provider: u.provider || "unknown",
        model: u.model || "unknown",
        totalTokens: toNum(u.totalTokens) || toNum(u.promptTokens) + toNum(u.completionTokens),
        durationMs: toNum(u.requestDuration),
        successful: u.successful !== false,
        createdAt: u.createdAt,
        username: u.username || u.user?.username,
      }));
    },
  });
}

export function useUnreadNotifications() {
  return useQuery({
    queryKey: ["notifications", "unread-count"],
    staleTime: STALE,
    refetchInterval: 2 * STALE,
    queryFn: async (): Promise<number> => {
      const r = await api.get("/notifications/unread-count");
      return toNum(r.count);
    },
  });
}
